import React from 'react';
import { GlobalLeaderboardEntry } from '../types';
import styles from '../../../../styles/LeaderBoard.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';

interface LeaderboardRowProps {
  entry: GlobalLeaderboardEntry;
  index: number;
  isHighlighted: boolean;
  isCurrentUser?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  isWorkMode?: boolean;
}

const LeaderboardRow: React.FC<LeaderboardRowProps> = ({
  entry,
  index,
  isHighlighted,
  isCurrentUser = false,
  onMouseEnter,
  onMouseLeave,
  isWorkMode = true
}) => {
  const getRankIcon = (rank: number) => {
    if (rank === 1) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5q0 .807-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33 33 0 0 1 2.5.5m.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935m10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935"/>
        </svg>
      );
    }
    if (rank === 2) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5q0 .807-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33 33 0 0 1 2.5.5m.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935m10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935"/>
        </svg>
      );
    }
    if (rank === 3) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5q0 .807-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33 33 0 0 1 2.5.5m.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935m10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935"/>
        </svg>
      );
    }
    return rank;
  };

  const getRankClass = (rank: number) => {
    if (rank === 1) return styles.rank1;
    if (rank === 2) return styles.rank2;
    if (rank === 3) return styles.rank3;
    return styles.rankOther;
  };


  const getInitials = (firstName?: string, lastName?: string, fullName?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (fullName) {
      const names = fullName.split(' ');
      return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = (user: GlobalLeaderboardEntry['user']) => {
    return user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
  };

  const formatDesignation = (designation?: string | null) => {
    if (!designation) return 'No Designation';
    return designation
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <tr
      className={`${styles.leaderboardRow} ${isHighlighted ? styles.highlight : ''} ${isCurrentUser ? styles.currentUserRow : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <td className={styles.rankCell}>
        <div className={`${styles.rankBadge} ${getRankClass(entry.rank)}`}>
          {getRankIcon(entry.rank)}
        </div>
      </td>
      <td className={styles.memberCell}>
        <div className={styles.memberInfo}>
          <div className={styles.memberAvatar}>
            {entry.user.avatar ? (
              <img src={entry.user.avatar} alt={getDisplayName(entry.user)} />
            ) : (
              getInitials(entry.user.first_name, entry.user.last_name, entry.user.full_name)
            )}
          </div>
          <div className={styles.memberDetails}>
            <div className={styles.memberName}>{getDisplayName(entry.user)}</div>
            <div className={styles.memberRole}>{formatDesignation(entry.user.designation)}</div>
            <div className={styles.memberStatsMobile}>
              <span>{entry.points.toLocaleString()} points</span> • <span>{entry.tasks_completed} tasks</span> • <span>{entry.quality_score || entry.participation_score}% quality</span>
            </div>
          </div>
        </div>
      </td>
      <td className={styles.groupCell}>
        <div className={styles.groupInfo}>
          <div 
            className={styles.groupColorIndicator}
            style={{ backgroundColor: entry.group.theme_color }}
          ></div>
          <div className={styles.groupName}>{entry.group.name}</div>
        </div>
      </td>
      <td className={styles.pointsCell}>
        <div className={styles.statValue}>{entry.points.toLocaleString()}</div>
        <div className={styles.statLabel}>POINTS</div>
      </td>
      <td className={styles.tasksCell}>
        <div className={styles.statValue}>{entry.tasks_completed}</div>
        <div className={styles.statLabel}>TASKS</div>
      </td>
      <td className={styles.qualityCell}>
        <div className={styles.statValue}>{entry.quality_score || entry.participation_score}%</div>
        <div className={styles.statLabel}>QUALITY</div>
      </td>
      {isWorkMode && (
        <td className={styles.actionsCell}>
          <button className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}>View</button>
        </td>
      )}
    </tr>
  );
};

export default LeaderboardRow;

